import React, {
  FC,
  PropsWithChildren,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Document, Page } from 'react-pdf';
import { pdfjs } from 'react-pdf';

import styles from './PDFViewer.module.scss';
import { ReportType } from '../../hooks/useReportGenerator';
import { PDFResult } from '../../models/pdfModal';
import { useReportStore } from '../../store/ReportStore';
import useRiskTransferStore from '../../store/RiskTransferStore';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

export interface PdfMenu {
  label: string;
  callback: () => void;
}

interface PDFViewerProps extends PropsWithChildren {
  type: ReportType;
  menus?: PdfMenu[];
  className?: string;
}

const PDFViewer: FC<PDFViewerProps> = ({
  menus = [],
  type,
  className = '',
  children = <></>,
}) => {
  const {
    principalReportPDF,
    riskControlReportPDF,
    riskManagementReportPDF,
    summaryReportPDF,
    isGeneratingReport,
  } = useReportStore();
  const { riskManagementSummaryReportPDF } = useRiskTransferStore();

  const menuRef = useRef<HTMLDivElement>(null);
  const pdfDocumentContainer = useRef<HTMLDivElement>(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [pdfFile, setPdfFile] = useState<PDFResult | null>(null);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);

  useEffect(() => {
    switch (type) {
      case 'principalReport':
        setPdfFile(principalReportPDF);
        break;
      case 'riskControlReport':
        setPdfFile(riskControlReportPDF);
        break;
      case 'riskManagementReport':
        setPdfFile(riskManagementReportPDF);
        break;
      case 'riskManagementSummaryReport':
        setPdfFile(riskManagementSummaryReportPDF);
        break;
      case 'summaryReport':
        setPdfFile(summaryReportPDF);
        break;
    }
  }, [
    principalReportPDF,
    riskControlReportPDF,
    riskManagementReportPDF,
    riskManagementSummaryReportPDF,
    summaryReportPDF,
  ]);

  // #region Methods
  const toggleMenu = () => {
    setMenuOpen((prev) => !prev);
  };

  const handleDownload = () => {
    setMenuOpen(false);
    alert('Download clicked!');
  };

  const pdfLoaded = (pdf: any) => {
    setTotalPages(pdf.numPages);
  };

  const nextPrevClicked = (isNext: boolean) => {
    if (isNext) {
      setCurrentPage((prev) => ++prev);
    } else {
      setCurrentPage((prev) => --prev);
    }

    if (pdfDocumentContainer.current) {
      pdfDocumentContainer.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };
  // #endregion Methods

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  return (
    <div className={`${styles.pdfViewer} ${className}`}>
      {/* Toolbar */}
      <div className={styles.pdfToolbar}>
        <div className={styles.fileInfo}>
          {pdfFile && `${pdfFile.filename}.pdf`}
        </div>
        <div className={styles.pageInfo}>
          {pdfFile
            ? `Page ${currentPage + 1} / ${totalPages}`
            : isGeneratingReport
            ? 'Generating report . . .'
            : 'No PDF Loaded'}
        </div>

        <div className={`${styles.actions}`} ref={menuRef}>
          <button
            className={styles.iconBtn}
            onClick={toggleMenu}
            title="More options"
          >
            <span className={styles.dots}>⋮</span>
          </button>

          {menuOpen && (
            <div className={styles.contextMenu}>
              <button onClick={handleDownload} disabled={!pdfFile}>
                Download
              </button>
              {menus.map((m) => {
                return (
                  <button
                    key={m.label}
                    onClick={() => {
                      setMenuOpen(false);
                      m.callback();
                    }}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* PDF Container */}
      <div className={`${styles.pdfContainer} ${!pdfFile && styles.noPDF__}`}>
        {!pdfFile && !isGeneratingReport && children}
        {pdfFile && (
          <div
            ref={pdfDocumentContainer}
            className={styles.pdfDocumentContainer}
          >
            <Document
              className={styles.pdfInnerContainer}
              file={pdfFile.blob}
              onLoadSuccess={pdfLoaded}
            >
              <Page
                className={`${styles.pdfPage} ${styles.hiddenPage}`}
                devicePixelRatio={2}
                // pageNumber={currentPage + 1}
                pageNumber={1}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              >
                {new Array(totalPages).fill('').map((_, ind) => {
                  return (
                    <Page
                      key={`page${ind}`}
                      className={`${styles.pdfPage} ${styles.pdfPageInner} ${
                        ind == currentPage
                          ? styles.current__
                          : ind < currentPage
                          ? styles.prev__
                          : styles.next__
                      }`}
                      devicePixelRatio={2}
                      pageNumber={ind + 1}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    ></Page>
                  );
                })}
              </Page>
            </Document>
          </div>
        )}

        {/* Floating navigation */}
        {pdfFile && (
          <button
            onClick={() => nextPrevClicked(false)}
            className={`${styles.navBtn} ${styles.prev}`}
            disabled={currentPage == 0}
          >
            &#8592;
          </button>
        )}
        {pdfFile && (
          <button
            onClick={() => nextPrevClicked(true)}
            className={`${styles.navBtn} ${styles.next}`}
            disabled={currentPage == totalPages - 1}
          >
            &#8594;
          </button>
        )}
      </div>
    </div>
  );
};

export default PDFViewer;
